import { WebStorageStateStore, type User } from 'oidc-client-ts'
import type { AuthProviderProps } from 'react-oidc-context'
import { config } from '@/config/env'

/**
 * Keycloak-Konfiguration für dieses Frontend.
 *
 * Verwendet wird Authorization Code Flow mit PKCE. Das ist der einzige
 * Flow, der für eine Browser-Anwendung ohne Client-Secret vertretbar ist.
 * Ein Implicit Flow käme ohne PKCE aus, gibt das Token aber über die
 * URL zurück, wo es im Verlauf und in Logs landet.
 */
export const oidcConfig: AuthProviderProps = {
  authority: config.oidcAuthority,
  client_id: config.oidcClientId,
  redirect_uri: `${window.location.origin}/auth/callback`,
  post_logout_redirect_uri: window.location.origin,
  scope: config.oidcScope,
  response_type: 'code',

  /*
   * Token im sessionStorage statt im localStorage.
   *
   * Der sessionStorage endet mit dem Tab. Das ist auf einem geteilten
   * oder unbeaufsichtigten Rechner die deutlich bessere Voreinstellung,
   * und die Weltmaschine kennt ohnehin keine langlebige Sitzung.
   */
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),

  /*
   * Stilles Erneuern, bevor das Token abläuft. Ohne das fliegt der
   * Benutzer mitten in der Sichtung raus, meist genau dann, wenn er
   * gerade etwas freigeben wollte.
   */
  automaticSilentRenew: true,
  monitorSession: false,

  /*
   * Räumt die Query-Parameter (?code=…&state=…) nach dem Login aus der
   * Adresszeile. Sonst bleibt der Autorisierungscode im Browserverlauf
   * stehen und ein Neuladen schlägt fehl.
   */
  onSigninCallback: (): void => {
    window.history.replaceState({}, document.title, window.location.pathname)
  },
}

/**
 * Rolle, die die Weltmaschine für praktisch jeden Endpunkt verlangt.
 *
 * Belegt in PandaControllerBase: [Authorize(Roles = "all:write")].
 * Wer sie nicht hat, kommt zwar durch den Login, bekommt aber von jedem
 * API-Aufruf ein 403 zurück. Deshalb prüfen wir sie im Frontend und
 * sagen es klar, statt den Benutzer gegen eine Wand laufen zu lassen.
 */
export const REQUIRED_ROLE = 'all:write'

interface KeycloakTokenClaims {
  readonly roles?: unknown
  readonly realm_access?: { readonly roles?: unknown }
  readonly preferred_username?: string
}

/**
 * Prüft, dass wirklich eine Liste von Strings vorliegt.
 *
 * Array.isArray allein verengt nur auf any[]. Der Inhalt eines Tokens
 * kommt von außen, also wird er auch wie Fremddaten behandelt.
 */
function isStringArray(value: unknown): value is readonly string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string')
}

/**
 * Liest die Realm-Rollen aus dem Token.
 *
 * Keycloak liefert sie je nach Mapper-Konfiguration entweder flach unter
 * "roles" oder verschachtelt unter "realm_access.roles". Die Weltmaschine
 * akzeptiert beides (siehe KeycloakRoleMapper), also tun wir das auch.
 */
export function extractRoles(user: User | null | undefined): readonly string[] {
  if (!user) return []

  const claims = user.profile as unknown as KeycloakTokenClaims
  if (isStringArray(claims.roles)) return claims.roles

  const realmRoles = claims.realm_access?.roles
  if (isStringArray(realmRoles)) return realmRoles

  return []
}

export function hasRequiredRole(user: User | null | undefined): boolean {
  return extractRoles(user).includes(REQUIRED_ROLE)
}
